import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { uk } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Trash, Clock, Phone } from "@phosphor-icons/react";

const MONTHS = ["Січень", "Лютий", "Березень", "Квітень", "Травень", "Червень", "Липень", "Серпень", "Вересень", "Жовтень", "Листопад", "Грудень"];

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Appointments() {
  const [date, setDate] = useState(new Date());
  const [all, setAll] = useState([]);
  const [clients, setClients] = useState([]);
  const [masters, setMasters] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", master_id: "", post: "Пост 1", time: "09:00", duration_min: 60, description: "" });

  const load = () => api.get("/appointments").then((r) => setAll(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); api.get("/masters").then((r) => setMasters(r.data)); }, []);

  const selected = toISO(date);
  const dayItems = all.filter((a) => a.date === selected).sort((x, y) => x.time.localeCompare(y.time));
  const marked = [...new Set(all.map((a) => a.date))].map((d) => new Date(d + "T00:00:00"));
  const monthPrefix = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  const monthItems = all
    .filter((a) => (a.date || "").startsWith(monthPrefix))
    .sort((x, y) => (x.date + x.time).localeCompare(y.date + y.time));

  const save = async () => {
    if (!form.client_id) return toast.error("Оберіть клієнта");
    try {
      await api.post("/appointments", { ...form, date: selected, duration_min: Number(form.duration_min) });
      toast.success("Запис створено"); setOpen(false);
      setForm({ client_id: "", master_id: "", post: "Пост 1", time: "09:00", duration_min: 60, description: "" }); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/appointments/${id}`); toast.success("Видалено"); load(); };

  return (
    <div>
      <PageHeader title="Календар / запис" subtitle="Запис клієнтів на обслуговування" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-4 flex justify-center">
          <Calendar mode="single" locale={uk} weekStartsOn={1} selected={date} onSelect={(d) => d && setDate(d)}
            modifiers={{ booked: marked }} modifiersClassNames={{ booked: "bg-primary/20 rounded-md font-bold" }} />
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-bold text-lg">
              {date.toLocaleDateString("uk-UA", { weekday: "long", day: "numeric", month: "long" })}
            </h3>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-appointment-btn"><Plus size={18} className="mr-1" /> Запис</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Новий запис — {selected}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Клієнт</Label>
                    <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="appt-client"><SelectValue placeholder="Оберіть" /></SelectTrigger>
                      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Час</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1.5" data-testid="appt-time" /></div>
                    <div><Label>Тривалість, хв</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Майстер</Label>
                      <Select value={form.master_id} onValueChange={(v) => setForm({ ...form, master_id: v })}>
                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{masters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Пост</Label><Input value={form.post} onChange={(e) => setForm({ ...form, post: e.target.value })} className="mt-1.5" /></div>
                  </div>
                  <div><Label>Опис робіт</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="appt-desc" /></div>
                </div>
                <DialogFooter><Button onClick={save} data-testid="save-appointment-btn">Записати</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-2">
            {dayItems.map((a) => (
              <div key={a.id} className="flex items-center gap-4 p-3 rounded-md bg-secondary/50 border border-border" data-testid={`appt-${a.id}`}>
                <div className="flex items-center gap-1.5 font-head font-bold text-primary w-16"><Clock size={16} /> {a.time}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{a.client_name}</div>
                  {a.phone && (
                    <a href={`tel:${a.phone}`} onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-0.5"
                      data-testid={`appt-phone-${a.id}`}>
                      <Phone size={13} /> {a.phone}
                    </a>
                  )}
                  <div className="text-xs text-muted-foreground">{a.description || "—"} · {a.post} · {a.master_name || "без майстра"}</div>
                </div>
                <button className="text-muted-foreground hover:text-destructive" onClick={() => del(a.id)} data-testid={`del-appt-${a.id}`}><Trash size={18} /></button>
              </div>
            ))}
            {dayItems.length === 0 && <div className="text-center text-muted-foreground py-10">На цей день записів немає</div>}
          </div>
        </Card>
      </div>

      <Card className="p-6 mt-5" data-testid="month-bookings">
        <h3 className="font-head font-bold text-lg mb-4">
          Бронювання на місяць — {MONTHS[date.getMonth()]} {date.getFullYear()}
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Дата</TableHead>
              <TableHead>Час</TableHead>
              <TableHead>Клієнт</TableHead>
              <TableHead>Телефон</TableHead>
              <TableHead>Опис</TableHead>
              <TableHead>Пост</TableHead>
              <TableHead>Майстер</TableHead>
              <TableHead className="text-right">Дії</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {monthItems.map((a) => (
              <TableRow key={a.id} data-testid={`month-row-${a.id}`}>
                <TableCell className="font-semibold whitespace-nowrap">
                  {new Date(a.date + "T00:00:00").toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit" })}
                </TableCell>
                <TableCell>{a.time}</TableCell>
                <TableCell>{a.client_name}</TableCell>
                <TableCell>
                  {a.phone ? <a href={`tel:${a.phone}`} className="text-primary hover:underline">{a.phone}</a> : "—"}
                </TableCell>
                <TableCell className="text-muted-foreground">{a.description || "—"}</TableCell>
                <TableCell>{a.post || "—"}</TableCell>
                <TableCell>{a.master_name || "—"}</TableCell>
                <TableCell className="text-right">
                  <button className="text-muted-foreground hover:text-destructive" onClick={() => del(a.id)} data-testid={`month-del-${a.id}`}><Trash size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
            {monthItems.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">На цей місяць бронювань немає</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
